/**
 * The `details.reason` literals the delivery API refuses a write with — one
 * definition the backend types what it throws with and the client keys its
 * copy off, so a reason added on one side and missed on the other is a
 * compile error rather than a silent generic toast.
 */

/** The 409s raised by a booking trying to JOIN an order (contract v11). */
export const DELIVERY_JOIN_CONFLICT_REASONS = [
  /** The order already landed; a booking cannot join an arrival. */
  'DELIVERY_NOT_OPEN',
  /** The order's line for this material measures it in a different unit (or
   *  in none). */
  'UNIT_MISMATCH',
  /** That line already stems from `MAX_DELIVERY_SOURCES` tasks and this
   *  one is new to it. (A task already on the line is never refused for
   *  room — a repeat join grows its share, contract v13.) */
  'SOURCES_FULL',
  /** The material would be a NEW line here, but another unsynced order
   *  already holds it — the one-open-line rule, refused by the index. */
  'LINE_OPEN_ELSEWHERE',
  /** The truck already carries `MAX_DELIVERY_LINES` materials. */
  'LINES_FULL',
] as const;

export type DeliveryJoinConflictReason = (typeof DELIVERY_JOIN_CONFLICT_REASONS)[number];

/** Every delivery-write 409 — one vocabulary, so a client reads
 *  `details.reason` exactly once. */
export const DELIVERY_CONFLICT_REASONS = [
  /** Another unsynced order already holds this material (`details.ref`). */
  'unsynced-rival',
  /** A synced order is history and cannot change. */
  'synced-locked',
  /** A patch tried to remove the order's last line — delete the order instead. */
  'LAST_LINE',
  /** The order's lines kept changing under a patch (concurrent joins) and the
   *  re-merged save still could not land — reload and save again. */
  'ORDER_CHANGED',
  ...DELIVERY_JOIN_CONFLICT_REASONS,
] as const;

export type DeliveryConflictReason = (typeof DELIVERY_CONFLICT_REASONS)[number];

/** The delivery 400s a client can name in its own words (the schema's own
 *  400s carry zod issues instead). */
export const DELIVERY_VALIDATION_REASONS = [
  /** A replied-by date or an ETA with no requested date (`details.field`). */
  'date-needs-requested',
  /** Two order dates out of sequence: requested ≤ replied-by ≤ ETA, and
   *  requested ≤ ETA (`details.field` is the later of the two). */
  'date-order',
  /** A line amount that rounds to 0 at one decimal (`details.field`). */
  'quantity-rounds-to-zero',
  /** A patch named the same line id twice. */
  'duplicate-line-id',
  /** A board reorder that would put a column after the Delivered system column. */
  'delivered-not-last',
  /** A sync's waste exceeds the leftover it is counted out of. */
  'waste-exceeds-remaining',
  /** A line for a material counted on site in another unit
   *  (`details.itemName`, `details.stockUnit`). */
  'unit-not-stock-unit',
] as const;

export type DeliveryValidationReason = (typeof DELIVERY_VALIDATION_REASONS)[number];
